import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A directory resource (school, hospital, religious place…) as returned by the backend. */
export interface Resource {
  id: number;
  name: string;
  category?: string | null;
  sub_category?: string | null;
  resource_type?: string | null;
  ownership?: string | null;
  location_level?: string | null;
  ec_location_level_id?: number | null;
  area_code?: string | null;
  address?: string | null;
  pincode?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  image_url?: string | null;
  incharge_name?: string | null;
  incharge_designation?: string | null;
  mobile?: string | null;
  alternate_phone?: string | null;
  email?: string | null;
  status?: 'Draft' | 'Published' | null;
  organization?: number | null;
  created_at?: string;
  updated_at?: string;
}

/** Body accepted by create/update (all optional on PATCH except name on create). */
export type ResourcePayload = Partial<Omit<Resource, 'id' | 'created_at' | 'updated_at'>> & {
  name: string;
};

type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

export interface ResourcesResponse {
  items: Resource[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ResourceListArgs {
  search?: string;
  category?: string;
  sub_category?: string;
  resource_type?: string;
  ownership?: string;
  area_code?: string;
  status?: string;
  organization?: number;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export const resourcesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getResources: build.query<ResourcesResponse, ResourceListArgs | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.resource}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.category ? { category: arg.category } : {}),
          ...(arg?.sub_category ? { sub_category: arg.sub_category } : {}),
          ...(arg?.resource_type ? { resource_type: arg.resource_type } : {}),
          ...(arg?.ownership ? { ownership: arg.ownership } : {}),
          ...(arg?.area_code ? { area_code: arg.area_code } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          ...(arg?.organization != null ? { organization: arg.organization } : {}),
          ...(arg?.ordering ? { ordering: arg.ordering } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<Resource>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['Resources'],
    }),
    getResource: build.query<Resource, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.resource}/${id}/` }),
      providesTags: ['Resources'],
    }),
    getResourceFilters: build.query<
      { categories: string[]; sub_categories: string[]; resource_types: string[] },
      void
    >({
      query: () => ({ url: `${ApiEndPoints.resourceFilters}/` }),
      transformResponse: (r: {
        categories?: string[];
        sub_categories?: string[];
        resource_types?: string[];
      } | null) => ({
        categories: r?.categories ?? [],
        sub_categories: r?.sub_categories ?? [],
        resource_types: r?.resource_types ?? [],
      }),
    }),
    createResource: build.mutation<Resource, ResourcePayload>({
      query: (body) => ({ url: `${ApiEndPoints.resource}/`, method: 'POST', body }),
      invalidatesTags: ['Resources'],
    }),
    updateResource: build.mutation<
      Resource,
      { id: number | string; patch: Partial<ResourcePayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.resource}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Resources'],
    }),
    deleteResource: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.resource}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Resources'],
    }),
  }),
});

export const {
  useGetResourcesQuery,
  useGetResourceQuery,
  useGetResourceFiltersQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,
} = resourcesApi;
